import { useMemo } from 'react';
import { Routes, UNSAFE_LocationContext as LocationContext } from 'react-router-dom';
import { appRoutes } from '../appRoutes';
import { useTabs } from './TabsContext';
import { TabPaneContext, type TabPaneInfo } from './TabPaneContext';
import type { LopTab } from './types';
import './TabHost.css';

// Renamed on import (UNSAFE_LocationContext -> LocationContext) purely to
// satisfy the `react/jsx-pascal-case` lint rule when used as a JSX tag below
// — same component, no behavior change.

/**
 * Replaces `<Outlet/>` in AppShell. The keep-alive heart of internal tabs
 * (plan §3.3): every open tab gets its OWN always-mounted pane, each running
 * its own `<Routes location={tab.location}>` against a FROZEN location
 * wrapped in `UNSAFE_LocationContext.Provider` — without that override,
 * `useLocation()`/`useSearchParams()` inside a hidden pane would keep reading
 * the router's live (active tab's) location instead of their own.
 * `useNavigate()` is unaffected — it reads `UNSAFE_NavigationContext`, which
 * we deliberately do NOT override, so a click inside any pane still drives
 * the real browser URL (verified in the Phase-0 spike).
 *
 * Hidden panes stay mounted with `visibility:hidden; position:absolute` (see
 * TabHost.css) — never `display:none`, which would reset AgGrid's row
 * virtualization/scroll and force a relayout on every switch — plus `inert`
 * so they can't be tabbed/clicked into while hidden.
 */
export function TabHost() {
  const { tabs, activeKey } = useTabs();

  return (
    <div className="lop-tab-host">
      {tabs.map((tab) => (
        <TabPane key={tab.key} tab={tab} isActive={tab.key === activeKey} />
      ))}
    </div>
  );
}

/**
 * One pane, split out from TabHost's `.map()` so it can call `useMemo` (a
 * bare arrow function passed to `.map` isn't a component — hooks aren't
 * allowed there). Wraps its subtree in TabPaneContext (plan §3.6:
 * useTabDirty/useTabActive/useTabTitle) memoized per (key, isActive) so a
 * dispatch elsewhere in the tabs tree doesn't force every pane's context
 * consumers to re-run their effects.
 */
function TabPane({ tab, isActive }: { tab: LopTab; isActive: boolean }) {
  const paneInfo = useMemo<TabPaneInfo>(() => ({ key: tab.key, isActive }), [tab.key, isActive]);
  return (
    <div className="lop-tab-pane" data-active={isActive} inert={!isActive}>
      <TabPaneContext.Provider value={paneInfo}>
        <LocationContext.Provider
          // `navigationType` is typed as the `Action` enum from the
          // 'history' package, which react-router-dom does not re-export
          // as a runtime value. 'POP' is the correct runtime value
          // (matches what useNavigationType() returns for a location
          // that isn't the result of an in-flight PUSH/REPLACE) — the
          // cast is only papering over the missing type export.
          value={{ location: tab.location, navigationType: 'POP' } as never}
        >
          <Routes location={tab.location}>{appRoutes}</Routes>
        </LocationContext.Provider>
      </TabPaneContext.Provider>
    </div>
  );
}
